import type { MouseEvent } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { Briefcase, Home, RefreshCw, TrendingUp, Trophy, type LucideIcon } from "lucide-react"
import type { PoolMembership } from "../../types/pools"
import { Button } from "../ui/Button"
import { cn } from "../../lib/utils"
import { useLocalization, type TranslationKey } from "../../lib/i18n"

type AppHeaderProps = {
  isAuthenticated: boolean
  displayName: string
  traderCode?: string
  avatarUrl?: string | null
  activePool: PoolMembership | null
  hasPools: boolean
  onLogin: () => void
  onEditProfile: () => void
  onRefreshStandings?: () => void
  onRefreshPortfolio?: () => void
  onRefreshPools?: () => void
  onRefreshResearch?: () => void
  showRefreshButton?: boolean
  hideAuthLoginCta?: boolean
}

type MobileTabItem = {
  to: string
  end?: boolean
  labelKey: TranslationKey
  icon: (props: MobileTabIconProps) => JSX.Element
  requiresPool?: boolean
}

type MobileTabIconProps = {
  className?: string
  strokeWidth?: number
  focused?: boolean
  "aria-hidden"?: boolean | "true" | "false"
}

function createLucideTabIcon(Icon: LucideIcon) {
  return function LucideTabIcon({ focused: _focused, ...props }: MobileTabIconProps) {
    return <Icon {...props} />
  }
}

function BracketTabIcon({ className, focused = false, "aria-hidden": ariaHidden }: MobileTabIconProps) {
  return (
    <svg className={className} viewBox="0 0 40 40" fill="none" aria-hidden={ariaHidden} focusable="false">
      <rect x="2" y="6" width="12" height="9" rx="2.5" stroke="currentColor" strokeWidth="3.2" />
      <rect x="2" y="25" width="12" height="9" rx="2.5" stroke="currentColor" strokeWidth="3.2" />
      <line x1="14" y1="10.5" x2="19" y2="10.5" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
      <line x1="14" y1="29.5" x2="19" y2="29.5" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
      <line x1="19" y1="10.5" x2="19" y2="29.5" stroke="currentColor" strokeWidth="3.2" />
      <line x1="19" y1="20" x2="24" y2="20" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
      <rect
        x="24"
        y="13"
        width={focused ? "15" : "14"}
        height="14"
        rx="3"
        fill={focused ? "currentColor" : "none"}
        stroke={focused ? "none" : "currentColor"}
        strokeWidth={focused ? undefined : "3.2"}
      />
      {focused ? (
        <path
          d="M27.5 20 L30 22.5 L35.5 17"
          stroke="#000000"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null}
    </svg>
  )
}

const mobileTabItems: MobileTabItem[] = [
  { to: "/", end: true, labelKey: "tabs.home", icon: createLucideTabIcon(Home) },
  { to: "/market", labelKey: "tabs.market", icon: createLucideTabIcon(TrendingUp), requiresPool: true },
  { to: "/portfolio", labelKey: "tabs.portfolio", icon: createLucideTabIcon(Briefcase), requiresPool: true },
  { to: "/standings", labelKey: "tabs.standings", icon: createLucideTabIcon(Trophy), requiresPool: true },
  { to: "/research", labelKey: "tabs.picks", icon: BracketTabIcon },
]

function getMobilePageTitle(pathname: string, t: (key: TranslationKey) => string) {
  if (pathname === "/market") return t("tabs.market")
  if (pathname === "/portfolio") return t("tabs.portfolio")
  if (pathname === "/standings") return t("tabs.standings")
  if (pathname === "/research") return t("tabs.picks")
  if (pathname === "/join") return t("home.join.title")
  if (pathname === "/support") return t("settings.support")
  if (pathname === "/privacy") return t("settings.privacyPolicy")
  if (pathname === "/terms") return t("common.terms")
  if (pathname === "/styleguide") return "Styleguide"
  return t("tabs.home")
}

export function AppHeader({
  isAuthenticated,
  displayName,
  traderCode,
  avatarUrl,
  activePool,
  hasPools,
  onLogin,
  onEditProfile,
  onRefreshStandings,
  onRefreshPortfolio,
  onRefreshPools,
  onRefreshResearch,
  showRefreshButton = false,
  hideAuthLoginCta = false,
}: AppHeaderProps) {
  const location = useLocation()
  const { t } = useLocalization()
  const canAccessTabs = !isAuthenticated || hasPools
  const mobilePageTitle = getMobilePageTitle(location.pathname, t)

  const handleRefresh = () => {
    const path = location.pathname
    if (path === "/market" && onRefreshStandings) {
      onRefreshStandings()
    } else if (path === "/portfolio" && onRefreshPortfolio) {
      onRefreshPortfolio()
    } else if (path === "/" && onRefreshPools) {
      onRefreshPools()
    } else if (path === "/research" && onRefreshResearch) {
      onRefreshResearch()
    }
  }

  const navBase =
    "inline-flex items-center px-3 lg:px-6 py-2 font-display text-[15px] font-semibold tracking-[0.01em] transition-all duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
  const navActive = "bg-brand text-black hover:bg-brand-hover"
  const navInactive = "text-foreground-muted hover:text-foreground hover:bg-background-elevated"
  const navDisabled = "pointer-events-none opacity-40"
  const activeTraderCode = activePool?.trader_code || traderCode || ""
  const avatarInitial = (activeTraderCode || displayName || "S").trim().charAt(0).toUpperCase()

  const renderProfileTrigger = () => (
    <button
      type="button"
      className="flex items-center gap-2 rounded-pill border border-transparent px-1 py-1 transition hover:border-border hover:bg-background-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      onClick={onEditProfile}
      aria-label={t("common.openProfileSettings")}
    >
      <span className="font-display text-body-sm font-bold tracking-wider text-foreground">{activeTraderCode}</span>
      <span className="flex h-10 w-10 overflow-hidden rounded-full border border-border-subtle bg-background-raised">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-body-sm font-bold text-foreground">
            {avatarInitial}
          </span>
        )}
      </span>
    </button>
  )

  return (
    <header className="bg-background shadow-[0_10px_30px_rgba(0,0,0,0.4)]">
      {/* Header bar */}
      <div className="mx-auto flex h-16 lg:h-24 max-w-[64rem] items-center px-3 md:px-4 lg:px-6 gap-4">
        {/* Desktop logo */}
        <NavLink to="/" className="hidden h-full shrink-0 items-center lg:flex">
          <img
            src="/img/sportex100-square.png"
            alt="Sport Exchange 100"
            className="h-full w-auto object-contain"
          />
        </NavLink>

        {/* Tablet logo */}
        <NavLink to="/" className="hidden h-12 shrink-0 items-center md:flex lg:hidden">
          <img
            src="/img/sportex100-square.png"
            alt="Sport Exchange 100"
            className="h-12 w-auto object-contain"
          />
        </NavLink>

        <h1 className="min-w-0 flex-1 truncate font-display text-[34px] font-bold uppercase leading-none text-foreground lg:hidden">
          {mobilePageTitle}
        </h1>

        {/* Desktop navigation */}
        <nav className="hidden lg:flex flex-1 items-center justify-start gap-1" aria-label={t("common.navigation")}>
          <NavLink to="/" end className={({ isActive }: { isActive: boolean }) => cn(navBase, isActive ? navActive : navInactive)}>
            {t("tabs.home")}
          </NavLink>
          <NavLink
            to="/market"
            className={({ isActive }: { isActive: boolean }) => cn(navBase, isActive ? navActive : navInactive, !canAccessTabs && navDisabled)}
            aria-disabled={!canAccessTabs}
            onClick={(event: MouseEvent) => !canAccessTabs && event.preventDefault()}
          >
            {t("tabs.market")}
          </NavLink>
          <NavLink
            to="/portfolio"
            className={({ isActive }: { isActive: boolean }) => cn(navBase, isActive ? navActive : navInactive, !canAccessTabs && navDisabled)}
            aria-disabled={!canAccessTabs}
            onClick={(event: MouseEvent) => !canAccessTabs && event.preventDefault()}
          >
            {t("tabs.portfolio")}
          </NavLink>
          <NavLink
            to="/standings"
            className={({ isActive }: { isActive: boolean }) => cn(navBase, isActive ? navActive : navInactive, !canAccessTabs && navDisabled)}
            aria-disabled={!canAccessTabs}
            onClick={(event: MouseEvent) => !canAccessTabs && event.preventDefault()}
          >
            {t("tabs.standings")}
          </NavLink>
          <NavLink
            to="/research"
            className={({ isActive }: { isActive: boolean }) => cn(navBase, isActive ? navActive : navInactive)}
          >
            {t("tabs.picks")}
          </NavLink>
        </nav>

        {/* DESKTOP: Refresh button */}
        {isAuthenticated && showRefreshButton && (
          <button
            type="button"
            className="hidden lg:flex h-10 items-center justify-center rounded-base border border-border-subtle bg-background-surface px-3 text-body text-foreground transition-all duration-200 hover:border-brand hover:bg-background-raised"
            onClick={handleRefresh}
            title={t("common.refresh")}
            aria-label={t("common.refresh")}
          >
            <RefreshCw className="h-5 w-5" aria-hidden="true" />
          </button>
        )}

        {/* DESKTOP: Trader code + avatar */}
        {isAuthenticated ? (
          <div className="ml-auto hidden items-center gap-2 lg:flex">{renderProfileTrigger()}</div>
        ) : (
          <div className="hidden lg:flex items-center gap-3 ml-auto">
            {!hideAuthLoginCta ? (
              <Button type="button" variant="primary" onClick={onLogin} className="!rounded-none">
                {t("auth.signIn")}
              </Button>
            ) : null}
          </div>
        )}

        {/* TABLET: Refresh button */}
        {isAuthenticated && showRefreshButton && (
          <button
            type="button"
            className="hidden md:flex h-10 w-10 items-center justify-center rounded-base border border-border-subtle bg-background-surface text-foreground transition-all duration-200 hover:border-brand hover:bg-background-raised lg:hidden"
            onClick={handleRefresh}
            title={t("common.refresh")}
            aria-label={t("common.refresh")}
          >
            <RefreshCw className="h-5 w-5" aria-hidden="true" />
          </button>
        )}

        {/* MOBILE/TABLET: Trader code + avatar */}
        {isAuthenticated ? (
          <div className="ml-auto flex items-center gap-2 lg:hidden">{renderProfileTrigger()}</div>
        ) : (
          <div className="flex lg:hidden items-center gap-2">
            {!hideAuthLoginCta ? (
              <Button type="button" variant="primary" onClick={onLogin} className="!rounded-none">
                {t("auth.signIn")}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 grid h-[calc(4rem+env(safe-area-inset-bottom))] grid-cols-5 border-t border-border-subtle bg-background/95 px-1 pb-[env(safe-area-inset-bottom)] pt-1 shadow-[0_-10px_30px_rgba(0,0,0,0.38)] backdrop-blur lg:hidden"
        aria-label={t("common.navigation")}
      >
        {mobileTabItems.map(({ to, end, labelKey, icon: Icon, requiresPool }) => {
          const isDisabled = Boolean(requiresPool && !canAccessTabs)

          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              aria-disabled={isDisabled}
              onClick={(event) => {
                if (isDisabled) {
                  event.preventDefault()
                }
              }}
              className={({ isActive }: { isActive: boolean }) =>
                cn(
                  "flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-md px-1 font-display text-[10px] font-semibold leading-none text-foreground-faint transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-background",
                  isActive && "bg-brand/15 text-brand",
                  !isActive && "hover:bg-background-surface hover:text-foreground",
                  isDisabled && "pointer-events-none opacity-40"
                )
              }
            >
              {({ isActive }: { isActive: boolean }) => (
                <>
                  <Icon className="h-5 w-5 shrink-0" strokeWidth={2.3} focused={isActive} aria-hidden="true" />
                  <span className="w-full truncate text-center">{t(labelKey)}</span>
                </>
              )}
            </NavLink>
          )
        })}
      </nav>
    </header>
  )
}
